import { useEffect, useRef, useCallback, useState } from 'react';
import { useEditorStore } from '../store';
import { useAuthStore } from '@/features/auth/store';
import * as projectsDB from '@/services/persistence/projects';

const AUTOSAVE_INTERVAL = 3_000; // 3 seconds

interface AutosaveState {
  projectId: string | null;
  projectName: string;
  saving: boolean;
  lastSaved: number | null;
}

export function useAutosave(initialProjectId?: string) {
  const userId = useAuthStore((s) => s.user?.id) ?? '';

  // Pick up pending metadata from a cloud-project open. ProjectsPage stashes
  // the id+name in the store before navigating; consume + clear here so the
  // title bar starts on the correct project name instead of "Untitled".
  const pendingId = useEditorStore.getState().pendingProjectId;
  const pendingName = useEditorStore.getState().pendingProjectName;

  const [state, setState] = useState<AutosaveState>({
    projectId: initialProjectId ?? pendingId ?? null,
    projectName: pendingName ?? 'Untitled Diagram',
    saving: false,
    lastSaved: null,
  });

  useEffect(() => {
    if (pendingId || pendingName) {
      useEditorStore.getState().setPendingProject(null, null);
    }
    // Run-once on mount — the values were already snapshotted into local state.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const dirtyRef = useRef(false);

  // Mark dirty on any store change
  useEffect(() => {
    return useEditorStore.subscribe(() => {
      dirtyRef.current = true;
    });
  }, []);

  // Load project on mount if ID provided
  useEffect(() => {
    if (!initialProjectId || !userId) return;
    projectsDB.getProject(initialProjectId, userId).then((project) => {
      if (project) {
        useEditorStore.getState().loadDiagram(project.diagram);
        setState((s) => ({
          ...s,
          projectId: project.id,
          projectName: project.name,
          lastSaved: project.updatedAt,
        }));
      }
    });
  }, [initialProjectId, userId]);

  const save = useCallback(async () => {
    if (!dirtyRef.current || !userId) return;
    dirtyRef.current = false;

    const diagram = useEditorStore.getState().getDiagram();
    if (diagram.nodes.length === 0 && diagram.edges.length === 0) return;

    setState((s) => ({ ...s, saving: true }));

    try {
      if (state.projectId) {
        const existing = await projectsDB.getProject(state.projectId, userId);
        if (existing) {
          await projectsDB.saveProject({ ...existing, diagram, name: state.projectName }, userId);
          setState((s) => ({ ...s, saving: false, lastSaved: Date.now() }));
          return;
        }
      }

      // Create new project scoped to current user
      const project = await projectsDB.createProject(state.projectName, diagram, userId);
      setState((s) => ({
        ...s,
        projectId: project.id,
        saving: false,
        lastSaved: Date.now(),
      }));
    } catch {
      setState((s) => ({ ...s, saving: false }));
    }
  }, [state.projectId, state.projectName, userId]);

  // Autosave interval
  useEffect(() => {
    timerRef.current = setInterval(save, AUTOSAVE_INTERVAL);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [save]);

  // Save on unmount
  useEffect(() => {
    return () => { save(); };
  }, [save]);

  const setProjectName = useCallback((name: string) => {
    setState((s) => ({ ...s, projectName: name }));
    dirtyRef.current = true;
  }, []);

  const saveNow = useCallback(() => {
    dirtyRef.current = true;
    return save();
  }, [save]);

  return {
    projectId: state.projectId,
    projectName: state.projectName,
    saving: state.saving,
    lastSaved: state.lastSaved,
    setProjectName,
    saveNow,
  };
}

export function formatLastSaved(ts: number | null): string {
  if (!ts) return '';
  const diff = Math.floor((Date.now() - ts) / 1000);
  if (diff < 5) return 'just now';
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  return `${Math.floor(diff / 3600)}h ago`;
}
